import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { useSearchParams } from "react-router-dom";

import {

  Button,

  TextField,

  ToggleButton,

  ToggleButtonGroup,

  IconButton,

  MenuItem,

} from "@mui/material";

import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";

import TextFieldsOutlinedIcon from "@mui/icons-material/TextFieldsOutlined";

import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";

import AddIcon from "@mui/icons-material/Add";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";

import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";

import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";

import {

  getGame,

  saveGameSettings,

  saveKnowledgeBaseItems,

  loadKbVideo,

  loadKbImage,

  loadPoster,

  getProfile,

} from "../../redux/asyncThunk/userActions";

import { sanitizePlainText } from "../../utils/inputValidation";

import { buildAnswerModePatch, normalizeContentMode } from "../../utils/contentMode";

import {

  getProjectStatusFromSettings,

  settingsFromProjectStatus,

  mergeGameSettings,

} from "../../utils/projectAccess";

import { readChatAccessGateFromSettings } from "../../utils/chatAccessGate";

import {

  KB_TOPIC_SUGGESTIONS,

  createTopicWithTitle,

  hasTopicWithTitle,

} from "../../utils/kbTopicSuggestions";

import {

  collectTopicDescendantIds,

  createEmptyTopic,

  canAddSubtopic,

  getRootTopics,

  getTopicChildren,

  getTopicPath,

  normalizeKbData,

} from "../../utils/kbTopics";

import ProjectTeamAccess from "../ProjectTeamAccess";

import ProjectDeleteSection from "../ProjectDeleteSection";

import { readAvailableMessengersFromSettings } from "../ProjectMessengerAvailability";

import { AUTH_FIELD_SX } from "../../pages/AuthLayout";

import { userSlice } from "../../redux/user/userSlice";

import InputFileUpload from "../UploadFile";
import MaxVideoLimitHint from "../MaxVideoLimitHint";

import ConfirmDeleteModal from "../ConfirmDeleteModal";

import "./knowledge-base-editor.css";



const { addMessage } = userSlice;

const fieldSx = { ...AUTH_FIELD_SX, maxWidth: "100%", width: "100%" };



function createEmptyItem(topicId) {

  return {

    id: `kb-${Date.now()}`,

    topicId,

    question: "",

    answerMode: "text",

    answerText: "",

    hasVideo: false,

    hasImage: false,

    screenpath: "",

    imagepath: "",

  };

}



function isItemComplete(item) {

  if (!item?.question?.trim()) return false;

  const mode = normalizeContentMode(item.answerMode, "text");

  if (mode === "video") return Boolean(item.hasVideo);

  if (mode === "image") return Boolean(item.hasImage);

  return Boolean(item.answerText?.trim());

}



function isTopicComplete(topic) {

  return Boolean(topic?.title?.trim());

}



function formatQuestionCount(count) {

  const n = Number(count) || 0;

  const mod10 = n % 10;

  const mod100 = n % 100;

  if (mod100 >= 11 && mod100 <= 14) return `${n} вопросов`;

  if (mod10 === 1) return `${n} вопрос`;

  if (mod10 >= 2 && mod10 <= 4) return `${n} вопроса`;

  return `${n} вопросов`;

}



function hasDuplicateTopicTitle(topics, topicId, title) {
  const topic = topics.find((entry) => entry.id === topicId);
  if (!topic) return false;
  const parentId = topic.parentId ?? null;
  const normalized = String(title ?? "").trim().toLowerCase();
  if (!normalized) return false;
  return topics.some(
    (entry) =>
      entry.id !== topicId &&
      (entry.parentId ?? null) === parentId &&
      String(entry.title ?? "").trim().toLowerCase() === normalized,
  );
}

function TopicListItem({
  topic,
  active,
  editing,
  editValue,
  onSelect,
  onEdit,
  onEditChange,
  onEditCommit,
  onEditCancel,
  onDelete,
}) {
  if (editing) {
    return (
      <li
        className={`kb-hub__list-item kb-hub__list-item--editing${
          active ? " kb-hub__list-item--active" : ""
        }`}
      >
        <TextField
          size="small"
          fullWidth
          autoFocus
          sx={fieldSx}
          value={editValue}
          onChange={(e) => onEditChange(sanitizePlainText(e.target.value, 120))}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onEditCommit();
            }
            if (e.key === "Escape") {
              e.preventDefault();
              onEditCancel();
            }
          }}
          onBlur={onEditCommit}
          placeholder="Название"
        />
      </li>
    );
  }

  return (
    <li
      className={`kb-hub__list-item${active ? " kb-hub__list-item--active" : ""}`}
    >
      <button type="button" className="kb-hub__list-btn" onClick={() => onSelect(topic.id)}>
        <FolderOutlinedIcon fontSize="small" className="kb-hub__list-icon" />
        <span className="kb-hub__list-label">
          {topic.title?.trim() || "Без названия"}
        </span>
      </button>
      {active ? (
        <div className="kb-hub__list-actions">
          <IconButton
            size="small"
            className="kb-hub__list-edit"
            aria-label="Редактировать"
            onClick={() => onEdit(topic.id)}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            className="kb-hub__tree-delete"
            aria-label="Удалить"
            onClick={() => onDelete(topic.id)}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </div>
      ) : null}
    </li>
  );
}

function QuestionListItem({ item, index, active, onSelect, onDelete }) {
  return (
    <li
      className={`kb-hub__items-list-item${active ? " kb-hub__items-list-item--active" : ""}`}
    >
      <button type="button" className="kb-hub__items-list-btn" onClick={() => onSelect(item.id)}>
        <span className="kb-hub__items-num">{index + 1}</span>
        <MenuBookOutlinedIcon fontSize="small" className="kb-hub__items-list-icon" />
        <span className="kb-hub__items-list-text">
          {item.question?.trim() || "Без названия"}
        </span>
      </button>
      {active ? (
        <div className="kb-hub__list-actions">
          <IconButton
            size="small"
            className="kb-hub__tree-delete"
            aria-label="Удалить вопрос"
            onClick={() => onDelete(item.id)}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </div>
      ) : null}
    </li>
  );
}



export default function KnowledgeBaseEditor({ game: initialGame, embedded = false }) {

  const dispatch = useDispatch();

  const [searchParams, setSearchParams] = useSearchParams();

  const scopedRootId = searchParams.get("topic");

  const game = useSelector((state) => state.user.game) ?? initialGame;

  const user = useSelector((state) => state.user?.data?.user);

  const profile = useSelector((state) => state.user?.profile);



  const initialData = useMemo(() => normalizeKbData(game), [game]);



  const [title, setTitle] = useState(game?.title ?? "");

  const [description, setDescription] = useState(game?.description ?? "");

  const [topics, setTopics] = useState(initialData.topics);

  const [items, setItems] = useState(initialData.items);

  const [activeRootTopicId, setActiveRootTopicId] = useState(null);

  const [activeSubtopicId, setActiveSubtopicId] = useState(null);

  const [activeItemId, setActiveItemId] = useState(null);
  const [draftSubtopicTitle, setDraftSubtopicTitle] = useState("");
  const [editingTopicId, setEditingTopicId] = useState(null);
  const [editingTopicTitle, setEditingTopicTitle] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);

  const [settings, setSettings] = useState({

    projectStatus: getProjectStatusFromSettings(game?.settings),

    completion_text: game?.settings?.completion_text ?? "",

    author_telegram: game?.settings?.author_telegram ?? "",

    allow_replay: game?.settings?.allow_replay !== false,

  });

  const [view, setView] = useState("editor");

  const [availableMessengers, setAvailableMessengers] = useState(

    readAvailableMessengersFromSettings(game?.settings),

  );

  const [chatAccessGate, setChatAccessGate] = useState(

    readChatAccessGateFromSettings(game?.settings),

  );



  const contentSaveTimerRef = useRef(null);

  const settingsSaveTimerRef = useRef(null);

  const lastSavedContentRef = useRef("");

  const lastSavedSettingsRef = useRef("");

  const loadedGameIdRef = useRef(null);

  const topicsRef = useRef(topics);

  const itemsRef = useRef(items);



  topicsRef.current = topics;

  itemsRef.current = items;



  const rootTopics = useMemo(() => getRootTopics(topics), [topics]);



  const activeRootTopic = useMemo(

    () => topics.find((topic) => topic.id === activeRootTopicId) ?? null,

    [topics, activeRootTopicId],

  );



  const subtopics = useMemo(

    () => (activeRootTopicId ? getTopicChildren(topics, activeRootTopicId) : []),

    [topics, activeRootTopicId],

  );



  const activeSubtopic = useMemo(

    () => topics.find((topic) => topic.id === activeSubtopicId) ?? null,

    [topics, activeSubtopicId],

  );



  const activeItem = useMemo(

    () => items.find((item) => item.id === activeItemId) ?? null,

    [items, activeItemId],

  );



  const topicItems = useMemo(

    () => items.filter((item) => item.topicId === activeSubtopicId),

    [items, activeSubtopicId],

  );



  const selectionPath = useMemo(() => {

    if (!activeSubtopicId) {

      return activeRootTopicId ? getTopicPath(topics, activeRootTopicId) : [];

    }

    return getTopicPath(topics, activeSubtopicId);

  }, [topics, activeRootTopicId, activeSubtopicId]);



  const syncRootTopicParam = useCallback(

    (rootTopicId) => {

      if (!rootTopicId) return;

      setSearchParams(

        (current) => {

          const next = new URLSearchParams(current);

          next.set("type", "knowledge_base");

          next.set("topic", rootTopicId);

          return next;

        },

        { replace: true },

      );

    },

    [setSearchParams],

  );



  const handleSelectRootTopic = useCallback(
    (topicId) => {
      setActiveRootTopicId(topicId);
      setActiveSubtopicId(null);
      setActiveItemId(null);
      setDraftSubtopicTitle("");
      syncRootTopicParam(topicId);
    },
    [syncRootTopicParam],
  );



  const handleSelectSubtopic = useCallback((topicId) => {

    setActiveSubtopicId(topicId);

    setActiveItemId(null);

  }, []);



  useEffect(() => {

    if (user?.id && !profile?.login) {

      dispatch(getProfile({ id: user.id }));

    }

  }, [dispatch, user?.id, profile?.login]);



  const buildSettingsSnapshot = useCallback(

    () =>

      JSON.stringify({

        title: title.trim(),

        description,

        settings,

        availableMessengers,

        chatAccessGate,

      }),

    [title, description, settings, availableMessengers, chatAccessGate],

  );



  const buildSavableContentSnapshot = useCallback(

    (topicsList, itemsList) =>

      JSON.stringify({

        topics: topicsList.filter(isTopicComplete),

        items: itemsList.filter(isItemComplete),

      }),

    [],

  );



  useEffect(() => {

    if (!game) return;

    setTitle(game.title ?? "");

    setDescription(game.description ?? "");

    setSettings({

      projectStatus: getProjectStatusFromSettings(game.settings),

      completion_text: game.settings?.completion_text ?? "",

      author_telegram: game.settings?.author_telegram ?? "",

      allow_replay: game.settings?.allow_replay !== false,

    });

    setAvailableMessengers(readAvailableMessengersFromSettings(game.settings));

    setChatAccessGate(readChatAccessGateFromSettings(game.settings));

    lastSavedSettingsRef.current = JSON.stringify({

      title: (game.title ?? "").trim(),

      description: game.description ?? "",

      settings: {

        projectStatus: getProjectStatusFromSettings(game.settings),

        completion_text: game.settings?.completion_text ?? "",

        author_telegram: game.settings?.author_telegram ?? "",

        allow_replay: game.settings?.allow_replay !== false,

      },

      availableMessengers: readAvailableMessengersFromSettings(game.settings),

      chatAccessGate: readChatAccessGateFromSettings(game.settings),

    });

  }, [game]);



  useEffect(() => {

    if (!game?._id) return;



    const normalized = normalizeKbData(game);

    const serverSnapshot = buildSavableContentSnapshot(

      normalized.topics,

      normalized.items,

    );

    const isNewGame = loadedGameIdRef.current !== game._id;



    if (isNewGame) {

      loadedGameIdRef.current = game._id;

      clearTimeout(contentSaveTimerRef.current);

      setTopics(normalized.topics);

      setItems(normalized.items);

      lastSavedContentRef.current = serverSnapshot;

      return;

    }



    if (serverSnapshot === lastSavedContentRef.current) return;

    const localSnapshot = buildSavableContentSnapshot(topics, items);
    if (localSnapshot !== lastSavedContentRef.current) return;

    const hasLocalDrafts =
      items.some((item) => !isItemComplete(item)) ||
      topics.some((topic) => !isTopicComplete(topic));
    if (hasLocalDrafts) return;

    clearTimeout(contentSaveTimerRef.current);

    setTopics(normalized.topics);

    setItems(normalized.items);

    lastSavedContentRef.current = serverSnapshot;

  }, [game, topics, items, buildSavableContentSnapshot]);



  useEffect(() => {

    if (!game?._id) return;



    const roots = getRootTopics(topics);



    if (!roots.length) {

      setActiveRootTopicId(null);

      setActiveSubtopicId(null);

      setActiveItemId(null);

      setSearchParams(

        (current) => {

          const next = new URLSearchParams(current);

          next.set("type", "knowledge_base");

          next.delete("topic");

          return next;

        },

        { replace: true },

      );

      return;

    }



    const validScopedRoot =

      scopedRootId && roots.some((topic) => topic.id === scopedRootId)

        ? scopedRootId

        : null;



    setActiveRootTopicId((current) => {

      if (current && roots.some((topic) => topic.id === current)) return current;

      return validScopedRoot ?? roots[0].id;

    });



    if (!validScopedRoot && roots[0]) {

      setSearchParams(

        (current) => {

          const next = new URLSearchParams(current);

          next.set("type", "knowledge_base");

          next.set("topic", roots[0].id);

          return next;

        },

        { replace: true },

      );

    }

  }, [game?._id, scopedRootId, topics, setSearchParams]);



  useEffect(() => {

    if (!activeSubtopicId) return;

    if (!topics.some((topic) => topic.id === activeSubtopicId)) {

      setActiveSubtopicId(null);

      setActiveItemId(null);

    }

  }, [topics, activeSubtopicId]);



  const persistContent = useCallback(

    async (topicsToSave, itemsToSave) => {

      if (!game?._id) return;



      const currentSnapshot = buildSavableContentSnapshot(

        topicsRef.current,

        itemsRef.current,

      );

      const requestedSnapshot = buildSavableContentSnapshot(

        topicsToSave,

        itemsToSave,

      );

      if (requestedSnapshot !== currentSnapshot) return;



      const completeTopics = topicsToSave.filter(isTopicComplete);

      const completeItems = itemsToSave.filter(isItemComplete);

      try {

        await dispatch(

          saveKnowledgeBaseItems({

            gameId: game._id,

            topics: completeTopics,

            items: completeItems,

          }),

        ).unwrap();



        if (

          buildSavableContentSnapshot(topicsRef.current, itemsRef.current) !==

          requestedSnapshot

        ) {

          return;

        }



        lastSavedContentRef.current = buildSavableContentSnapshot(

          topicsToSave,

          itemsToSave,

        );

      } catch {

        dispatch(addMessage({ type: "error", message: "Не удалось сохранить" }));

      }

    },

    [dispatch, game?._id, buildSavableContentSnapshot],

  );



  const persistSettings = useCallback(async () => {

    if (!game?._id) return;

    const trimmedTitle = title.trim();

    try {

      await dispatch(

        saveGameSettings({

          game: game._id,

          title: trimmedTitle,

          description,

          program: trimmedTitle,

          category: "",

          settings: mergeGameSettings(game?.settings, {

            ...settingsFromProjectStatus(settings.projectStatus, game?.settings),

            contentType: "knowledge_base",

            completion_text: settings.completion_text,

            author_telegram: settings.author_telegram,

            allow_replay: settings.allow_replay,

            availableMessengers,

            ...chatAccessGate,

          }),

        }),

      ).unwrap();

      lastSavedSettingsRef.current = buildSettingsSnapshot();

    } catch {

      dispatch(addMessage({ type: "error", message: "Не удалось сохранить настройки" }));

    }

  }, [

    dispatch,

    game?._id,

    game?.settings,

    title,

    description,

    settings,

    availableMessengers,

    chatAccessGate,

    buildSettingsSnapshot,

  ]);



  useEffect(() => {

    const serialized = buildSavableContentSnapshot(topics, items);

    if (serialized === lastSavedContentRef.current || !game?._id) return;



    clearTimeout(contentSaveTimerRef.current);

    contentSaveTimerRef.current = setTimeout(() => {

      persistContent(topicsRef.current, itemsRef.current);

    }, 800);



    return () => clearTimeout(contentSaveTimerRef.current);

  }, [topics, items, game?._id, persistContent, buildSavableContentSnapshot]);



  useEffect(() => {

    const serialized = buildSettingsSnapshot();

    if (serialized === lastSavedSettingsRef.current || !game?._id) return;



    clearTimeout(settingsSaveTimerRef.current);

    settingsSaveTimerRef.current = setTimeout(() => {

      persistSettings();

    }, 800);



    return () => clearTimeout(settingsSaveTimerRef.current);

  }, [

    title,

    description,

    settings,

    availableMessengers,

    chatAccessGate,

    game?._id,

    buildSettingsSnapshot,

    persistSettings,

  ]);



  const updateTopic = (topicId, patch) => {
    if (!topicId) return;
    setTopics((prev) =>
      prev.map((topic) => (topic.id === topicId ? { ...topic, ...patch } : topic)),
    );
  };

  const startEditTopic = (topicId) => {
    const topic = topics.find((entry) => entry.id === topicId);
    if (!topic) return;

    if ((topic.parentId ?? null) === null) {
      setActiveRootTopicId(topicId);
      setActiveSubtopicId(null);
      setActiveItemId(null);
      setDraftSubtopicTitle("");
      syncRootTopicParam(topicId);
    } else {
      setActiveSubtopicId(topicId);
      setActiveItemId(null);
    }

    setEditingTopicId(topicId);
    setEditingTopicTitle(topic.title ?? "");
  };

  const cancelEditTopic = () => {
    setEditingTopicId(null);
    setEditingTopicTitle("");
  };

  const commitEditTopic = () => {
    if (!editingTopicId) return;

    const trimmed = sanitizePlainText(editingTopicTitle, 120);
    const current = topics.find((entry) => entry.id === editingTopicId);

    if (!trimmed || !current) {
      cancelEditTopic();
      return;
    }

    if (trimmed === String(current.title ?? "").trim()) {
      cancelEditTopic();
      return;
    }

    if (hasDuplicateTopicTitle(topics, editingTopicId, trimmed)) {
      dispatch(
        addMessage({
          type: "info",
          message: `«${trimmed}» уже используется на этом уровне`,
        }),
      );
      return;
    }

    updateTopic(editingTopicId, { title: trimmed });
    cancelEditTopic();
  };

  const topicListItemProps = (topic) => ({
    topic,
    editing: editingTopicId === topic.id,
    editValue: editingTopicId === topic.id ? editingTopicTitle : "",
    onEditChange: setEditingTopicTitle,
    onEditCommit: commitEditTopic,
    onEditCancel: cancelEditTopic,
    onEdit: startEditTopic,
  });

  const updateActiveItem = (patch) => {

    if (!activeItemId) return;

    setItems((prev) =>

      prev.map((item) => (item.id === activeItemId ? { ...item, ...patch } : item)),

    );

  };



  const handleAddRootTopic = () => {

    const next = createEmptyTopic(null);

    setTopics((prev) => [...prev, next]);

    handleSelectRootTopic(next.id);

  };



  const handleAddTopicFromSuggestion = (suggestedTitle) => {

    const nextTitle = sanitizePlainText(suggestedTitle, 120);

    if (!nextTitle) return;



    if (hasTopicWithTitle(topics, nextTitle)) {

      dispatch(

        addMessage({

          type: "info",

          message: `Тема «${nextTitle}» уже есть`,

        }),

      );

      const existing = topics.find(

        (topic) =>

          (topic.parentId ?? null) === null &&

          topic.title.trim().toLowerCase() === nextTitle.toLowerCase(),

      );

      if (existing) {

        handleSelectRootTopic(existing.id);

      }

      return;

    }



    const next = createTopicWithTitle(nextTitle, null);

    setTopics((prev) => [...prev, next]);

    handleSelectRootTopic(next.id);

  };



  const availableTopicSuggestions = useMemo(

    () =>

      KB_TOPIC_SUGGESTIONS.filter(

        (suggestion) => !hasTopicWithTitle(topics, suggestion),

      ),

    [topics],

  );



  const handleCreateSubtopicFromDraft = () => {
    if (!activeRootTopicId) {
      dispatch(
        addMessage({
          type: "error",
          message: "Сначала выберите основную тему",
        }),
      );
      return;
    }

    if (!canAddSubtopic(topics, activeRootTopicId)) {
      dispatch(
        addMessage({
          type: "error",
          message: "Подтемы можно добавлять только внутри основных тем",
        }),
      );
      return;
    }

    const subtopicTitle = sanitizePlainText(draftSubtopicTitle, 120);
    if (!subtopicTitle) {
      dispatch(
        addMessage({
          type: "error",
          message: "Введите название подтемы",
        }),
      );
      return;
    }

    const duplicate = subtopics.some(
      (topic) => topic.title.trim().toLowerCase() === subtopicTitle.toLowerCase(),
    );
    if (duplicate) {
      dispatch(
        addMessage({
          type: "info",
          message: `Подтема «${subtopicTitle}» уже есть в этой теме`,
        }),
      );
      const existing = subtopics.find(
        (topic) => topic.title.trim().toLowerCase() === subtopicTitle.toLowerCase(),
      );
      if (existing) {
        setActiveSubtopicId(existing.id);
        setActiveItemId(null);
      }
      return;
    }

    const next = createTopicWithTitle(subtopicTitle, activeRootTopicId);
    setTopics((prev) => [...prev, next]);
    setActiveSubtopicId(next.id);
    setActiveItemId(null);
    setDraftSubtopicTitle("");
  };

  const handleSubtopicDraftKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleCreateSubtopicFromDraft();
    }
  };


  const handleDeleteRootTopic = (topicId) => {

    const idsToRemove = collectTopicDescendantIds(topics, topicId);

    const nextTopics = topics.filter((entry) => !idsToRemove.has(entry.id));

    setTopics(nextTopics);

    setItems((prev) => prev.filter((item) => !idsToRemove.has(item.topicId)));



    if (idsToRemove.has(editingTopicId)) {
      cancelEditTopic();
    }

    if (idsToRemove.has(activeRootTopicId) || idsToRemove.has(activeSubtopicId)) {

      setActiveSubtopicId(null);

      setActiveItemId(null);

      const remainingRoots = getRootTopics(nextTopics);

      if (remainingRoots[0]) {

        handleSelectRootTopic(remainingRoots[0].id);

      } else {

        setActiveRootTopicId(null);

        setSearchParams(

          (current) => {

            const params = new URLSearchParams(current);

            params.delete("topic");

            return params;

          },

          { replace: true },

        );

      }

    }



    dispatch(addMessage({ type: "info", message: "Тема удалена" }));

  };



  const handleDeleteSubtopic = (topicId) => {

    const idsToRemove = collectTopicDescendantIds(topics, topicId);

    const nextTopics = topics.filter((entry) => !idsToRemove.has(entry.id));

    setTopics(nextTopics);

    setItems((prev) => prev.filter((item) => !idsToRemove.has(item.topicId)));



    if (idsToRemove.has(editingTopicId)) {
      cancelEditTopic();
    }

    if (idsToRemove.has(activeSubtopicId)) {

      setActiveSubtopicId(null);

      setActiveItemId(null);

    }



    dispatch(addMessage({ type: "info", message: "Подтема удалена" }));

  };



  const handleAddItem = async () => {

    if (!activeSubtopicId) {

      dispatch(

        addMessage({

          type: "error",

          message: "Выберите подтему для добавления вопроса",

        }),

      );

      return;

    }



    if (activeItem && !isItemComplete(activeItem)) {

      dispatch(

        addMessage({

          type: "error",

          message: "Заполните вопрос и ответ, прежде чем добавлять новый",

        }),

      );

      return;

    }



    clearTimeout(contentSaveTimerRef.current);

    await persistContent(topics, items);

    const next = createEmptyItem(activeSubtopicId);

    setItems((prev) => [...prev, next]);

    setActiveItemId(next.id);

  };



  const handleViewChange = async (nextView) => {

    if (view === "settings" && nextView !== "settings") {

      clearTimeout(settingsSaveTimerRef.current);

      await persistSettings();

    }

    setView(nextView);

  };



  const handleDeleteItem = (itemId) => {
    setItems((prev) => prev.filter((item) => item.id !== itemId));
    if (activeItemId === itemId) {
      setActiveItemId(null);
    }
  };

  const requestDeleteRootTopic = (topicId) => {
    const topic = topics.find((entry) => entry.id === topicId);
    setPendingDelete({
      kind: "root",
      id: topicId,
      label: topic?.title?.trim() || "Без названия",
    });
  };

  const requestDeleteSubtopic = (topicId) => {
    const topic = topics.find((entry) => entry.id === topicId);
    setPendingDelete({
      kind: "subtopic",
      id: topicId,
      label: topic?.title?.trim() || "Без названия",
    });
  };

  const requestDeleteItem = (itemId) => {
    const item = items.find((entry) => entry.id === itemId);
    setPendingDelete({
      kind: "item",
      id: itemId,
      label: item?.question?.trim() || "Без названия",
    });
  };

  const handleConfirmPendingDelete = () => {
    if (!pendingDelete) return;

    if (pendingDelete.kind === "root") {
      handleDeleteRootTopic(pendingDelete.id);
    } else if (pendingDelete.kind === "subtopic") {
      handleDeleteSubtopic(pendingDelete.id);
    } else if (pendingDelete.kind === "item") {
      handleDeleteItem(pendingDelete.id);
      dispatch(addMessage({ type: "info", message: "Вопрос удалён" }));
    }

    setPendingDelete(null);
  };

  const pendingDeleteCopy = useMemo(() => {
    if (!pendingDelete) return null;

    if (pendingDelete.kind === "root") {
      return {
        title: "Удалить тему?",
        message: `Тема «${pendingDelete.label}» будет удалена.`,
        detail: "Все подтемы и вопросы внутри этой темы также будут удалены.",
      };
    }

    if (pendingDelete.kind === "subtopic") {
      return {
        title: "Удалить подтему?",
        message: `Подтема «${pendingDelete.label}» будет удалена.`,
        detail: "Все вопросы и ответы в этой подтеме также будут удалены.",
      };
    }

    return {
      title: "Удалить вопрос?",
      message: `Вопрос «${pendingDelete.label}» будет удалён.`,
      detail: "Ответ и загруженные материалы по этому вопросу будут удалены без возможности восстановления.",
    };
  }, [pendingDelete]);

  const handleVideoUpload = (event) => {

    const file = event.target.files?.[0];

    if (!file || !activeItem || !user?.id || !game?._id) return;



    dispatch(

      loadKbVideo({

        userId: user.id,

        gameId: game._id,

        itemId: activeItem.id,

        file,

      }),

    ).then((result) => {

      if (result.payload?.kbItems) {

        setItems((prev) => {

          const merged = prev.map((item) => {

            const saved = result.payload.kbItems.find((entry) => entry.id === item.id);

            return saved ?? item;

          });

          lastSavedContentRef.current = buildSavableContentSnapshot(topics, merged);

          return merged;

        });

      }

    });

  };



  const handleImageUpload = (event) => {

    const file = event.target.files?.[0];

    if (!file || !activeItem || !user?.id || !game?._id) return;



    dispatch(

      loadKbImage({

        userId: user.id,

        gameId: game._id,

        itemId: activeItem.id,

        file,

      }),

    ).then((result) => {

      if (result.payload?.kbItems) {

        setItems((prev) => {

          const merged = prev.map((item) => {

            const saved = result.payload.kbItems.find((entry) => entry.id === item.id);

            return saved ?? item;

          });

          lastSavedContentRef.current = buildSavableContentSnapshot(topics, merged);

          return merged;

        });

      }

    });

  };



  const handlePoster = (event, gameId) => {

    const file = event.target.files?.[0];

    if (!file) return;

    const reader = new FileReader();

    reader.readAsDataURL(file);

    reader.onloadend = () => {

      dispatch(

        loadPoster({

          gameId,

          base64String: reader.result,

          onSucces: () => dispatch(getGame({ gameId })),

        }),

      );

    };

  };



  const renderProjectSettings = () => (

    <section className="kb-hub__settings-card">

      <h3>Настройки проекта</h3>

      <TextField

        label="Название для сотрудников"

        size="small"

        fullWidth

        sx={fieldSx}

        value={title}

        onChange={(e) => setTitle(sanitizePlainText(e.target.value, 120))}

        placeholder="База знаний"

      />

      <TextField

        label="Приветственный текст"

        size="small"

        fullWidth

        multiline

        minRows={3}

        sx={fieldSx}

        value={description}

        onChange={(e) =>

          setDescription(sanitizePlainText(e.target.value, 2000))

        }

      />

      <div className="kb-hub__settings-row">

        <TextField

          select

          label="Статус"

          size="small"

          sx={{ ...fieldSx, flex: 1 }}

          value={settings.projectStatus}

          onChange={(e) =>

            setSettings((prev) => ({

              ...prev,

              projectStatus: e.target.value,

            }))

          }

        >

          <MenuItem value="draft">Черновик</MenuItem>

          <MenuItem value="published">Опубликован</MenuItem>

          <MenuItem value="hidden">Скрыт</MenuItem>

        </TextField>

        <TextField

          label="Telegram HR"

          size="small"

          sx={{ ...fieldSx, flex: 1.4 }}

          value={settings.author_telegram}

          onChange={(e) =>

            setSettings((prev) => ({

              ...prev,

              author_telegram: sanitizePlainText(e.target.value, 80),

            }))

          }

        />

      </div>

      <div className="kb-editor__poster">

        <InputFileUpload

          label="Обложка"

          accept="image/*"

          onChange={(event) => handlePoster(event, game._id)}

        />

      </div>

    </section>

  );



  const renderTopicChips = () => {

    if (!availableTopicSuggestions.length) return null;



    return (

      <div className="kb-hub__topic-chips">

        <span className="kb-hub__chips-label">Предложения:</span>

        {availableTopicSuggestions.map((suggestion) => (

          <button

            key={suggestion}

            type="button"

            className="kb-hub__topic-chip"

            onClick={() => handleAddTopicFromSuggestion(suggestion)}

          >

            + {suggestion}

          </button>

        ))}

      </div>

    );

  };



  return (

    <div className={`kb-hub${embedded ? " kb-hub--embedded" : ""}`}>

      {embedded ? (

        <header className="kb-hub__hero">

          <div className="kb-hub__hero-icon">

            <MenuBookOutlinedIcon />

          </div>

          <div className="kb-hub__hero-copy">

            <h1 className="kb-hub__title">База знаний</h1>

            <p className="kb-hub__lead">

              Одна база на аккаунт. Сначала создайте основные темы, затем

              подтемы и вопросы с ответами для сотрудников.

            </p>

          </div>

        </header>

      ) : null}



      <div className="kb-hub__toolbar">

        <Button

          variant="outlined"

          size="small"

          className={`kb-hub__tab${view === "editor" ? " kb-hub__tab--active" : ""}`}

          startIcon={<EditOutlinedIcon />}

          onClick={() => handleViewChange("editor")}

        >

          Редактор

        </Button>

        <Button

          variant="outlined"

          size="small"

          className={`kb-hub__tab${view === "settings" ? " kb-hub__tab--active" : ""}`}

          startIcon={<SettingsOutlinedIcon />}

          onClick={() => handleViewChange("settings")}

        >

          Настройки

        </Button>

      </div>



      <div className="kb-hub__meta">

        <span className="kb-hub__meta-path">

          {title.trim() || "Без названия"}

          {selectionPath.length ? (

            <>

              {" "}

              /{" "}

              <strong>

                {selectionPath

                  .map((topic) => topic.title?.trim() || "Без названия")

                  .join(" / ")}

              </strong>

            </>

          ) : null}

        </span>

        {view === "editor" ? (

          <span className="kb-hub__meta-stat">

            {formatQuestionCount(items.filter(isItemComplete).length)} готово

          </span>

        ) : null}

      </div>



      {view === "settings" ? (

        <div className="kb-hub__settings">

          {renderProjectSettings()}

          <ProjectTeamAccess

            gameId={game._id}

            value={availableMessengers}

            onChange={setAvailableMessengers}

            chatAccessSettings={chatAccessGate}

            onChatAccessChange={setChatAccessGate}

          />

          <ProjectDeleteSection label="База знаний будет удалена" />

        </div>

      ) : (

        <div className="kb-hub__workspace">

          <section className="kb-hub__panel">

            <div className="kb-hub__panel-head">

              <div>

                <h2 className="kb-hub__panel-title">Структура базы знаний</h2>

                <p className="kb-hub__panel-lead">

                  Слева — основные темы, рядом подтемы, справа вопросы и ответы

                  выбранной подтемы.

                </p>

              </div>

            </div>



            <div className="kb-hub__layout kb-hub__layout--three">

              <aside className="kb-hub__col kb-hub__col--roots">

                <div className="kb-hub__col-head">

                  <h3 className="kb-hub__col-title">Основные темы</h3>

                </div>



                {!rootTopics.length ? (

                  <p className="kb-hub__empty">

                    База пока пустая. Добавьте тему или выберите подсказку ниже.

                  </p>

                ) : (
                  <ul className="kb-hub__list kb-hub__list-scroll">
                    {rootTopics.map((topic) => (
                      <TopicListItem
                        key={topic.id}
                        active={activeRootTopicId === topic.id}
                        onSelect={handleSelectRootTopic}
                        onDelete={requestDeleteRootTopic}
                        {...topicListItemProps(topic)}
                      />

                    ))}

                  </ul>

                )}



                <Button

                  size="small"

                  variant="outlined"

                  className="kb-hub__col-action"

                  startIcon={<AddIcon />}

                  onClick={handleAddRootTopic}

                >

                  Тема

                </Button>



                {renderTopicChips()}

              </aside>



              <aside className="kb-hub__col kb-hub__col--subtopics">

                <div className="kb-hub__col-head">

                  <h3 className="kb-hub__col-title">Подтемы</h3>

                </div>



                {activeRootTopic ? (
                  <>
                    <p className="kb-hub__col-context">
                      Тема:{" "}
                      <strong>
                        {activeRootTopic.title?.trim() || "Без названия"}
                      </strong>
                    </p>

                    <TextField
                      label="Новая подтема"
                      size="small"
                      fullWidth
                      sx={fieldSx}
                      value={draftSubtopicTitle}
                      onChange={(e) =>
                        setDraftSubtopicTitle(sanitizePlainText(e.target.value, 120))
                      }
                      onKeyDown={handleSubtopicDraftKeyDown}
                      placeholder="Например: Товары"
                    />

                    {!subtopics.length ? (
                      <p className="kb-hub__empty">
                        Введите название и нажмите «Подтема» или Enter.
                      </p>
                    ) : (
                      <ul className="kb-hub__list kb-hub__list-scroll">
                        {subtopics.map((topic) => (
                          <TopicListItem
                            key={topic.id}
                            active={activeSubtopicId === topic.id}
                            onSelect={handleSelectSubtopic}
                            onDelete={requestDeleteSubtopic}
                            {...topicListItemProps(topic)}
                          />
                        ))}
                      </ul>
                    )}

                    <Button
                      size="small"
                      variant="outlined"
                      className="kb-hub__col-action"
                      startIcon={<AddIcon />}
                      onClick={handleCreateSubtopicFromDraft}
                      disabled={!canAddSubtopic(topics, activeRootTopicId)}
                    >
                      Подтема
                    </Button>
                  </>
                ) : (

                  <p className="kb-hub__placeholder">

                    Выберите основную тему слева.

                  </p>

                )}

              </aside>



              <section className="kb-hub__col kb-hub__col--questions">

                <div className="kb-hub__col-head">

                  <h3 className="kb-hub__col-title">Вопросы и ответы</h3>

                </div>



                {activeSubtopic ? (
                  <>
                    <p className="kb-hub__col-context">
                      {selectionPath
                        .map((topic) => topic.title?.trim() || "Без названия")
                        .join(" / ")}
                    </p>

                    <div className="kb-hub__items-layout">
                      <div className="kb-hub__items-nav">
                        {topicItems.length === 0 ? (
                          <p className="kb-hub__empty">
                            В этой подтеме пока нет вопросов.
                          </p>
                        ) : (
                          <ul className="kb-hub__items-list kb-hub__list-scroll">
                            {topicItems.map((item, index) => (
                              <QuestionListItem
                                key={item.id}
                                item={item}
                                index={index}
                                active={activeItemId === item.id}
                                onSelect={setActiveItemId}
                                onDelete={requestDeleteItem}
                              />
                            ))}
                          </ul>
                        )}
                      </div>

                      <div className="kb-hub__items-editor">

                        {activeItem ? (

                          <div className="kb-hub__item-form">

                            <TextField

                              label="Вопрос сотрудника"

                              size="small"

                              fullWidth

                              multiline

                              minRows={2}

                              sx={fieldSx}

                              value={activeItem.question}

                              onChange={(e) =>

                                updateActiveItem({

                                  question: sanitizePlainText(e.target.value, 500),

                                })

                              }

                              placeholder="Например: Как оформить отпуск?"

                            />



                            <span className="kb-hub__field-label">

                              Ответ администратора

                            </span>

                            <ToggleButtonGroup

                              exclusive

                              size="small"

                              value={normalizeContentMode(activeItem.answerMode, "text")}

                              onChange={(_, value) => {

                                if (value) {

                                  updateActiveItem(buildAnswerModePatch(value));

                                }

                              }}

                              className="app-dialog__type-toggle kb-editor__answer-mode"

                            >

                              <ToggleButton value="text">

                                <TextFieldsOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />

                                Текст

                              </ToggleButton>

                              <ToggleButton value="video">

                                <VideocamOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />

                                Видео

                              </ToggleButton>

                              <ToggleButton value="image">

                                <ImageOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />

                                Картинка

                              </ToggleButton>

                            </ToggleButtonGroup>



                            {normalizeContentMode(activeItem.answerMode, "text") === "text" ? (

                              <TextField

                                label="Ответ"

                                size="small"

                                fullWidth

                                multiline

                                minRows={5}

                                sx={fieldSx}

                                value={activeItem.answerText}

                                onChange={(e) =>

                                  updateActiveItem({

                                    answerText: sanitizePlainText(e.target.value, 4000),

                                  })

                                }

                                placeholder="Текст, который увидит сотрудник при открытии базы знаний"

                              />

                            ) : null}



                            {normalizeContentMode(activeItem.answerMode, "text") === "video" ? (

                              <div className="kb-editor__video-upload">

                                <InputFileUpload

                                  label="Видео-ответ"

                                  accept="video/*"

                                  onChange={handleVideoUpload}

                                />

                                {activeItem.hasVideo ? (

                                  <p className="kb-editor__video-status">Видео загружено</p>

                                ) : null}

                                <MaxVideoLimitHint availableMessengers={availableMessengers} />

                              </div>

                            ) : null}



                            {normalizeContentMode(activeItem.answerMode, "text") === "image" ? (

                              <div className="kb-editor__video-upload">

                                <InputFileUpload

                                  label="Картинка-ответ"

                                  accept="image/*"

                                  onChange={handleImageUpload}

                                />

                                {activeItem.hasImage ? (

                                  <p className="kb-editor__video-status">Картинка загружена</p>

                                ) : null}

                              </div>

                            ) : null}



                            <Button

                              size="small"

                              variant="contained"

                              className="app-btn app-btn--primary app-editor__add-question-btn kb-editor__add-submit"

                              startIcon={<AddIcon />}

                              onClick={handleAddItem}

                            >

                              Добавить

                            </Button>

                          </div>

                        ) : (

                          <div className="kb-editor__questions-placeholder">

                            <p>Выберите вопрос из списка или создайте новый.</p>

                            <Button

                              size="small"

                              variant="contained"

                              className="app-btn app-btn--primary app-editor__add-question-btn"

                              startIcon={<AddIcon />}

                              onClick={handleAddItem}

                            >

                              Добавить вопрос

                            </Button>

                          </div>

                        )}

                      </div>

                    </div>

                  </>

                ) : activeRootTopic ? (

                  <p className="kb-hub__placeholder">

                    Выберите подтему, чтобы добавить вопросы и ответы.

                  </p>

                ) : (

                  <p className="kb-hub__placeholder">

                    Сначала выберите основную тему и подтему.

                  </p>

                )}

              </section>

            </div>

          </section>

        </div>

      )}

      <ConfirmDeleteModal
        open={Boolean(pendingDelete && pendingDeleteCopy)}
        title={pendingDeleteCopy?.title ?? ""}
        message={pendingDeleteCopy?.message}
        detail={pendingDeleteCopy?.detail}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleConfirmPendingDelete}
      />
    </div>
  );
}

